import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { confirmSignIn, signIn } from "aws-amplify/auth";
import { Helmet } from "react-helmet";
import {
  postPasswordResetConfirm,
  postPasswordResetRequest,
} from "../services/callApi";
import { getAuthenticatedUser } from "../utils/Auth";

const LOGIN_VIEW = "login";
const RESET_REQUEST_VIEW = "reset-request";
const RESET_CONFIRM_VIEW = "reset-confirm";
const NEW_PASSWORD_REQUIRED_VIEW = "new-password-required";

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getApiErrorMessage = (error, fallbackMessage) =>
  error?.response?.data?.message || error?.message || fallbackMessage;

function PasswordField({
  id,
  label,
  value,
  onChange,
  autoComplete,
  disabled,
  showPassword,
  onToggleVisibility,
  error,
  helperText,
}) {
  return (
    <TextField
      id={id}
      name={id}
      label={label}
      type={showPassword ? "text" : "password"}
      autoComplete={autoComplete}
      value={value}
      onChange={onChange}
      disabled={disabled}
      error={Boolean(error)}
      helperText={helperText}
      required
      fullWidth
      margin="normal"
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              aria-label={showPassword ? `${label}を非表示` : `${label}を表示`}
              edge="end"
              onClick={onToggleVisibility}
              disabled={disabled}
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
}

const Login = () => {
  const [view, setView] = useState(LOGIN_VIEW);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [requiredNewPassword, setRequiredNewPassword] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [confirmationCode, setConfirmationCode] = useState("");
  const [resetNewPassword, setResetNewPassword] = useState("");
  const [resetNewPasswordConfirmation, setResetNewPasswordConfirmation] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordConfirmationError, setPasswordConfirmationError] = useState("");
  const [formError, setFormError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showRequiredNewPassword, setShowRequiredNewPassword] = useState(false);
  const [showResetNewPassword, setShowResetNewPassword] = useState(false);
  const [showResetNewPasswordConfirmation, setShowResetNewPasswordConfirmation] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const redirectAuthenticatedUser = async () => {
      try {
        const authenticatedUser = await getAuthenticatedUser();
        if (isMounted && authenticatedUser?.userId) {
          window.location.href = "/manager";
        }
      } catch (error) {
        // 未ログイン時はログイン画面を表示するため、エラーを通知しない。
      }
    };

    redirectAuthenticatedUser();
    return () => {
      isMounted = false;
    };
  }, []);

  const clearMessages = () => {
    setEmailError("");
    setPasswordConfirmationError("");
    setFormError("");
    setSuccessMessage("");
  };

  const switchView = (nextView) => {
    clearMessages();
    setView(nextView);
  };

  const handleSignIn = async (event) => {
    event.preventDefault();
    clearMessages();
    setIsSubmitting(true);

    try {
      const response = await signIn({ username, password });
      if (response.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
        setView(NEW_PASSWORD_REQUIRED_VIEW);
        return;
      }

      const authenticatedUser = await getAuthenticatedUser();
      if (authenticatedUser?.userId) {
        window.location.href = "/manager";
        return;
      }
      setFormError("ログイン状態を確認できませんでした。もう一度お試しください。");
    } catch (error) {
      setFormError(getApiErrorMessage(error, "ログインに失敗しました。"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRequiredPasswordChange = async (event) => {
    event.preventDefault();
    clearMessages();
    setIsSubmitting(true);

    try {
      await confirmSignIn({ challengeResponse: requiredNewPassword });
      window.location.href = "/manager";
    } catch (error) {
      setFormError(getApiErrorMessage(error, "パスワードの変更に失敗しました。"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordResetRequest = async (event) => {
    event.preventDefault();
    clearMessages();
    const normalizedEmail = resetEmail.trim();

    if (!emailPattern.test(normalizedEmail)) {
      setEmailError("メールアドレスを正しく入力してください。");
      return;
    }

    setIsSubmitting(true);
    try {
      await postPasswordResetRequest(normalizedEmail);
      setResetEmail(normalizedEmail);
      setSuccessMessage("再設定コードをメールアドレスへ送信しました。");
      setView(RESET_CONFIRM_VIEW);
    } catch (error) {
      setEmailError(getApiErrorMessage(error, "再設定コードの送信に失敗しました。"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordResetConfirm = async (event) => {
    event.preventDefault();
    clearMessages();

    if (resetNewPassword !== resetNewPasswordConfirmation) {
      setPasswordConfirmationError("新しいパスワードが一致しません。");
      return;
    }

    setIsSubmitting(true);
    try {
      await postPasswordResetConfirm(resetEmail, confirmationCode, resetNewPassword);
      setConfirmationCode("");
      setResetNewPassword("");
      setResetNewPasswordConfirmation("");
      setSuccessMessage("パスワードを再設定しました。新しいパスワードでログインしてください。");
      setView(LOGIN_VIEW);
    } catch (error) {
      setFormError(getApiErrorMessage(error, "パスワードの再設定に失敗しました。"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderFormMessage = () => (
    <>
      {successMessage && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {successMessage}
        </Alert>
      )}
      {formError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {formError}
        </Alert>
      )}
    </>
  );

  const renderLogin = () => (
    <Box component="form" onSubmit={handleSignIn} noValidate>
      <TextField
        id="username"
        name="username"
        label="ユーザー名"
        autoComplete="username"
        autoFocus
        value={username}
        onChange={(event) => setUsername(event.target.value)}
        disabled={isSubmitting}
        required
        fullWidth
        margin="normal"
      />
      <PasswordField
        id="password"
        label="パスワード"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        autoComplete="current-password"
        disabled={isSubmitting}
        showPassword={showPassword}
        onToggleVisibility={() => setShowPassword((current) => !current)}
      />
      {renderFormMessage()}
      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={isSubmitting}
        sx={{ mt: 3, py: 1.2, fontWeight: 700 }}
      >
        ログイン
      </Button>
      <Box sx={{ textAlign: "center", mt: 1 }}>
        <Button
          type="button"
          variant="text"
          disabled={isSubmitting}
          onClick={() => switchView(RESET_REQUEST_VIEW)}
        >
          パスワードを忘れた方はこちら
        </Button>
      </Box>
    </Box>
  );

  const renderRequiredPasswordChange = () => (
    <Box component="form" onSubmit={handleRequiredPasswordChange} noValidate>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        初回ログインのため、新しいパスワードを設定してください。
      </Typography>
      <PasswordField
        id="required-new-password"
        label="新しいパスワード"
        value={requiredNewPassword}
        onChange={(event) => setRequiredNewPassword(event.target.value)}
        autoComplete="new-password"
        disabled={isSubmitting}
        showPassword={showRequiredNewPassword}
        onToggleVisibility={() => setShowRequiredNewPassword((current) => !current)}
      />
      {renderFormMessage()}
      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={isSubmitting}
        sx={{ mt: 3, py: 1.2, fontWeight: 700 }}
      >
        パスワードを設定する
      </Button>
    </Box>
  );

  const renderResetRequest = () => (
    <Box component="form" onSubmit={handlePasswordResetRequest} noValidate>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
        登録済みのメールアドレスへ、再設定用の確認コードを送信します。
      </Typography>
      <TextField
        id="reset-email"
        name="email"
        label="登録メールアドレス"
        type="email"
        autoComplete="email"
        autoFocus
        value={resetEmail}
        onChange={(event) => {
          setResetEmail(event.target.value);
          setEmailError("");
        }}
        disabled={isSubmitting}
        error={Boolean(emailError)}
        helperText={emailError}
        required
        fullWidth
        margin="normal"
      />
      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={isSubmitting}
        sx={{ mt: 3, py: 1.2, fontWeight: 700 }}
      >
        再設定コードを送信
      </Button>
      <Box sx={{ textAlign: "center", mt: 1 }}>
        <Button
          type="button"
          variant="text"
          disabled={isSubmitting}
          onClick={() => switchView(LOGIN_VIEW)}
        >
          ログインに戻る
        </Button>
      </Box>
    </Box>
  );

  const renderResetConfirm = () => (
    <Box component="form" onSubmit={handlePasswordResetConfirm} noValidate>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
        メールで届いた確認コードと、新しいパスワードを入力してください。
      </Typography>
      <TextField
        id="confirmation-code"
        name="confirmation-code"
        label="確認コード"
        autoComplete="one-time-code"
        autoFocus
        value={confirmationCode}
        onChange={(event) => setConfirmationCode(event.target.value)}
        disabled={isSubmitting}
        required
        fullWidth
        margin="normal"
      />
      <PasswordField
        id="reset-new-password"
        label="新しいパスワード"
        value={resetNewPassword}
        onChange={(event) => setResetNewPassword(event.target.value)}
        autoComplete="new-password"
        disabled={isSubmitting}
        showPassword={showResetNewPassword}
        onToggleVisibility={() => setShowResetNewPassword((current) => !current)}
      />
      <PasswordField
        id="reset-new-password-confirmation"
        label="新しいパスワード（確認）"
        value={resetNewPasswordConfirmation}
        onChange={(event) => {
          setResetNewPasswordConfirmation(event.target.value);
          setPasswordConfirmationError("");
        }}
        autoComplete="new-password"
        disabled={isSubmitting}
        showPassword={showResetNewPasswordConfirmation}
        onToggleVisibility={() => setShowResetNewPasswordConfirmation((current) => !current)}
        error={passwordConfirmationError}
        helperText={passwordConfirmationError}
      />
      {renderFormMessage()}
      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={isSubmitting}
        sx={{ mt: 3, py: 1.2, fontWeight: 700 }}
      >
        パスワードを再設定する
      </Button>
      <Box sx={{ textAlign: "center", mt: 1 }}>
        <Button
          type="button"
          variant="text"
          disabled={isSubmitting}
          onClick={() => switchView(RESET_REQUEST_VIEW)}
        >
          メールアドレスを変更する
        </Button>
      </Box>
    </Box>
  );

  const heading =
    view === RESET_REQUEST_VIEW
      ? "パスワードを再設定"
      : view === RESET_CONFIRM_VIEW
        ? "確認コードを入力"
        : view === NEW_PASSWORD_REQUIRED_VIEW
          ? "新しいパスワードを設定"
          : "管理者ログイン";

  return (
    <Box
      component="main"
      sx={{
        py: { xs: 5, sm: 8 },
        minHeight: "calc(100vh - 160px)",
        background:
          "radial-gradient(circle at top right, rgba(25, 118, 210, 0.14), transparent 38%), #f6f8fb",
      }}
    >
      <Helmet>
        <title>エンジニア適性テスト | 管理者ログイン</title>
      </Helmet>
      <Container maxWidth="xs">
        <Paper
          elevation={0}
          sx={{
            px: { xs: 2.5, sm: 4 },
            py: { xs: 3.5, sm: 4.5 },
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            borderTop: "4px solid",
            borderTopColor: "primary.main",
            boxShadow: "0 18px 42px rgba(22, 41, 72, 0.12)",
          }}
        >
          <Typography
            variant="overline"
            color="primary"
            sx={{ letterSpacing: "0.12em", fontWeight: 700 }}
          >
            ENGINEERING APTITUDE TEST
          </Typography>
          {/* <Typography component="h1" variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
            エンジニア適性テスト
          </Typography> */}
          <Typography component="h1" variant="h5" sx={{ mt: 2, fontWeight: 600 }}>
            {heading}
          </Typography>
          {view === LOGIN_VIEW && renderLogin()}
          {view === NEW_PASSWORD_REQUIRED_VIEW && renderRequiredPasswordChange()}
          {view === RESET_REQUEST_VIEW && renderResetRequest()}
          {view === RESET_CONFIRM_VIEW && renderResetConfirm()}
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;
